/**
 * Collection JSON Loader
 * 
 * Reusable module for loading and accessing collection data from a CDN JSON file.
 * Can be used by any section that needs collection product data.
 * 
 * Usage:
 *   const loader = new CollectionJsonLoader(jsonUrl);
 *   await loader.load();
 *   const collection = loader.getCollectionByHandle('collection-handle');
 *   const products = collection?.products || [];
 */

if (typeof window.CollectionJsonLoader === 'undefined') {
window.CollectionJsonLoader = class CollectionJsonLoader {
  constructor(jsonUrl) {
    this.jsonUrl = jsonUrl;
    this.data = null;
    this.loadPromise = null;
    this.error = null;
  }

  /**
   * Load JSON data from CDN
   * @returns {Promise<Object>} The loaded JSON data
   */
  async load() {
    // Return existing promise if already loading
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Return data if already loaded
    if (this.data) {
      return Promise.resolve(this.data);
    }

    // Start loading
    this.loadPromise = fetch(this.jsonUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load JSON: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        this.data = data;
        this.loadPromise = null;
        return data;
      })
      .catch(error => {
        this.error = error;
        this.loadPromise = null;
        console.error('CollectionJsonLoader: Error loading JSON:', error);
        throw error;
      });

    return this.loadPromise;
  }

  /**
   * Get collection by handle
   * @param {string} handle - Collection handle (e.g., 'propane-natural-gas-furnaces')
   * @returns {Object|null} Collection object or null if not found
   */
  getCollectionByHandle(handle) {
    if (!this.data || !this.data.collections) {
      return null;
    }

    return this.data.collections.find(
      collection => collection.collectionHandle === handle
    ) || null;
  }

  /**
   * Get all collections
   * @returns {Array} Array of all collections
   */
  getAllCollections() {
    return this.data?.collections || [];
  }

  /**
   * Transform JSON product to match Liquid product structure
   * @param {Object} jsonProduct - Product from JSON
   * @returns {Object} Transformed product object
   */
  transformProduct(jsonProduct) {
    // Extract numeric ID from GID (e.g., "gid://shopify/Product/9081892077806" -> 9081892077806)
    const extractId = (gid) => {
      if (typeof gid === 'string' && gid.includes('/')) {
        return parseInt(gid.split('/').pop(), 10);
      }
      return gid;
    };

    // Transform metafields from flat object to nested structure
    const transformMetafields = (metafields) => {
      const transformed = {};
      if (!metafields) return transformed;

      Object.keys(metafields).forEach(key => {
        // Handle namespace.key format (e.g., "custom.heating_capacity_btus_")
        if (key.includes('.')) {
          const [namespace, metafieldKey] = key.split('.');
          if (!transformed[namespace]) {
            transformed[namespace] = {};
          }
          transformed[namespace][metafieldKey] = {
            value: metafields[key]
          };
        } else {
          // Direct key access
          transformed[key] = {
            value: metafields[key]
          };
        }
      });

      return transformed;
    };

    // Transform image object
    const transformImage = (imageObj) => {
      if (!imageObj || !imageObj.url) return null;
      
      return {
        url: imageObj.url,
        alt: imageObj.alt || '',
        width: imageObj.width || 0,
        height: imageObj.height || 0,
        // Add image_url method for compatibility
        image_url: function(width) {
          // If width specified, try to modify URL (Shopify CDN supports width parameter)
          if (width && this.url.includes('cdn.shopify.com')) {
            const urlObj = new URL(this.url);
            urlObj.searchParams.set('width', width);
            return urlObj.toString();
          }
          return this.url;
        }
      };
    };

    // Transform variants
    const transformVariants = (variants) => {
      if (!variants || !Array.isArray(variants)) return [];
      
      return variants.map(variant => ({
        id: extractId(variant.id),
        title: variant.title || 'Default Title',
        price: variant.price || 0,
        compare_at_price: variant.compare_at_price || null,
        available: variant.available !== false,
        sku: variant.sku || ''
      }));
    };

    const productId = extractId(jsonProduct.id);
    const productUrl = (() => {
      const raw = jsonProduct.url || (jsonProduct.handle ? `/products/${jsonProduct.handle}` : '');
      if (!raw) return '';
      if (raw.startsWith('http://') || raw.startsWith('https://')) {
        try {
          return new URL(raw).pathname;
        } catch (e) {
          return raw;
        }
      }
      return raw;
    })();
    const transformed = {
      id: productId,
      handle: jsonProduct.handle || '',
      title: jsonProduct.title || '',
      vendor: jsonProduct.vendor || '',
      product_type: jsonProduct.product_type || '',
      tags: jsonProduct.tags || [],
      url: productUrl,
      available: jsonProduct.available !== false,
      price: jsonProduct.price || 0,
      compare_at_price: jsonProduct.compare_at_price || null,
      metafields: transformMetafields(jsonProduct.metafields),
      featured_media: transformImage(jsonProduct.featured_image),
      featured_image: transformImage(jsonProduct.featured_image), // Alias for compatibility
      secondary_image: transformImage(jsonProduct.secondary_image),
      variants: transformVariants(jsonProduct.variants),
      // Add helper properties
      selected_or_first_available_variant: transformVariants(jsonProduct.variants)[0] || null
    };

    return transformed;
  }

  /**
   * Get transformed products for a collection
   * @param {string} handle - Collection handle
   * @returns {Array} Array of transformed product objects
   */
  getProductsByHandle(handle) {
    const collection = this.getCollectionByHandle(handle);
    if (!collection || !collection.products) {
      return [];
    }

    return collection.products.map(product => this.transformProduct(product));
  }

  /**
   * Check if data is loaded
   * @returns {boolean}
   */
  isLoaded() {
    return this.data !== null;
  }

  /**
   * Check if there was an error
   * @returns {boolean}
   */
  hasError() {
    return this.error !== null;
  }

  /**
   * Get error message
   * @returns {string|null}
   */
  getError() {
    return this.error?.message || null;
  }
};
} // end duplicate-load guard

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CollectionJsonLoader;
}
